/**
 * First pass — Eva spells each word once, with no per-word feedback.
 * Implements spec §4.2.
 *
 * What Eva sees, one word at a time:
 *   - A small "word 3 of 10" dot row at the top.
 *   - "Hear it again" button (audio auto-plays on each word's arrival).
 *   - "Hear a sentence" button only if the parent added a sentence.
 *   - One large text input. Done button below.
 *   - No correctness feedback yet — spec is explicit: results come after
 *     all 10 are submitted, not per-word.
 *
 * Lifecycle:
 *   - On mount we read the active session from SessionContext (populated
 *     by FlashCardScreen, PastWeeksScreen, or sessionPersist's
 *     rehydrate-on-boot for spec §5.3 mid-session resume). If there's
 *     no session at all (deep-link / refresh after a long gap), we
 *     bounce to home.
 *   - Each word's spelling is recorded into SessionContext via
 *     recordFirstPassAttempt as soon as Done is tapped. The raw typed
 *     string is stored; the case-insensitive trim happens in
 *     spellingCompare at scoring time.
 *   - Tapping Done starts a ~400ms neutral transition (spec §10 default)
 *     during which the form is dimmed and inert, then advances. After
 *     the 10th word the next destination is the results screen.
 *
 * Pumpkin is not visible here — task-focus screen per spec §7.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import type { FormEvent } from "react";

import { Button } from "@/components/Button";
import { useNavigation } from "@/context/NavigationContext";
import { useSession } from "@/context/SessionContext";
import { cancelSpeech, speak } from "@/lib/speech";
import "./FirstPassScreen.css";

const AUTO_ADVANCE_MS = 400;

export function FirstPassScreen() {
  const { navigate } = useNavigation();
  const { session, recordFirstPassAttempt } = useSession();
  // Resume support (spec §5.3): start at the first un-attempted word.
  // Attempts are recorded on Done, in order, so the count of recorded
  // entries == the index of the next word she should see.
  const [index, setIndex] = useState(() =>
    session === null ? 0 : Math.min(
      Object.keys(session.firstPassAttempts).length,
      Math.max(session.words.length - 1, 0),
    ),
  );
  const [input, setInput] = useState("");
  const [advancing, setAdvancing] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // Bounce to home if there's no session (e.g. the user navigated here
  // directly with no flash-card to populate it). If every word's been
  // attempted (edge case — she finished but the screen saved before
  // its own navigate to results landed), jump straight to results.
  useEffect(() => {
    if (session === null || session.words.length === 0) {
      navigate("home");
      return;
    }
    if (Object.keys(session.firstPassAttempts).length >= session.words.length) {
      navigate("results");
    }
  }, [session, navigate]);

  const word = session?.words[index];
  const wordId = word?.id;
  const total = session?.words.length ?? 0;

  // Speak the current word on arrival. Re-fires only when the word
  // identity changes (not on every keystroke). The cleanup cancels any
  // in-flight utterance — important under React StrictMode's
  // double-invoke in dev where without it the same word would queue twice.
  useEffect(() => {
    if (!session || !word) return;
    speak(word.spelling, session.settings);
    return () => cancelSpeech();
    // Intentionally keying on wordId only: settings changing mid-session
    // shouldn't re-speak; advancing flag intentionally doesn't re-fire.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wordId]);

  // Refocus the input on each new word so the keyboard stays open.
  useEffect(() => {
    if (!advancing) inputRef.current?.focus();
  }, [index, advancing]);

  const handleSayAgain = useCallback(() => {
    if (!session || !word) return;
    speak(word.spelling, session.settings);
  }, [session, word]);

  const handleHearSentence = useCallback(() => {
    if (!session || !word || word.exampleSentence === null) return;
    speak(word.exampleSentence, session.settings);
  }, [session, word]);

  const handleDone = useCallback(() => {
    if (!session || !word || advancing) return;
    recordFirstPassAttempt(word.id, input);
    cancelSpeech();
    setAdvancing(true);
    window.setTimeout(() => {
      if (index === session.words.length - 1) {
        navigate("results");
      } else {
        setInput("");
        setIndex((i) => i + 1);
        setAdvancing(false);
      }
    }, AUTO_ADVANCE_MS);
  }, [session, word, advancing, recordFirstPassAttempt, input, index, navigate]);

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    handleDone();
  };

  if (!session || !word) {
    // Either the redirect effect is about to fire or we're between
    // sessions. Render nothing rather than a flash of a placeholder.
    return null;
  }

  return (
    <main className="first-pass" data-advancing={advancing ? "true" : "false"}>
      <div
        className="first-pass__progress"
        role="progressbar"
        aria-valuemin={1}
        aria-valuemax={total}
        aria-valuenow={index + 1}
        aria-label={`Word ${index + 1} of ${total}`}
      >
        {session.words.map((w, i) => (
          <span
            key={w.id}
            className={
              "first-pass__dot" +
              (i < index ? " is-done" : "") +
              (i === index ? " is-current" : "")
            }
            aria-hidden="true"
          />
        ))}
      </div>

      <form className="first-pass__form" onSubmit={handleSubmit}>
        <div className="first-pass__audio-row">
          <button
            type="button"
            className="first-pass__audio-btn"
            onClick={handleSayAgain}
            aria-label="Hear the word again"
            disabled={advancing}
          >
            <span aria-hidden="true">🔊</span> Say it again
          </button>
          {word.exampleSentence !== null && (
            <button
              type="button"
              className="first-pass__audio-btn"
              onClick={handleHearSentence}
              aria-label="Hear a sentence"
              disabled={advancing}
            >
              <span aria-hidden="true">♪</span> Hear a sentence
            </button>
          )}
        </div>

        <input
          ref={inputRef}
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          className="first-pass__input"
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="off"
          spellCheck={false}
          inputMode="text"
          enterKeyHint="done"
          aria-label="Type the word here"
          disabled={advancing}
        />

        <Button type="submit" size="large" disabled={advancing}>
          Done
        </Button>
      </form>
    </main>
  );
}
